import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthContext.js';
import { useTitle } from '../lib/use-title.js';
import { cn } from '../lib/cn.js';
import { type, button, field, banner } from './theme.js';
import { WaveField } from './WaveField.js';

/**
 * 工厂端登录页(H1b,2026-09-09 按用户定的方向重做)。
 *
 * ── 形态:左深右白,一屏,不滚 ─────────────────────────────────
 *
 * 参考的是顶级 SaaS 的登录页长相:**右边只有一件事 —— 扫码**;左边是品牌、
 * 一句话、两张真截图斜着放、底层一张 three.js 的点阵。整页可见中文不到 60 字。
 *
 * 上一版是「销售页 + 登录卡」:三个价值屏、三档合作方式、四段说明。用户的话是
 * 「像菜市场老太太絮叨」「太土」。这一页是工厂老板对产品的第一眼,要的是有分量,
 * 不是信息完整 —— 要说的话在 link4a.com 官网说。守卫钉着可见字数不许回涨。
 *
 * ── 截图是真的,只露焦点区 ───────────────────────────────────
 *
 * `public/portal-shots/` 那几张是 playwright 从跑起来的工厂端抓的。
 * 这次不再整页缩略(上一版缩到 650 宽,字糊成一团),而是 16:10 的框、
 * 图放到 150% 宽对齐左上,于是会话页露出的是「侧栏 + 聊天列」,字看得清。
 *
 * ── 不变的东西 ───────────────────────────────────────────────
 *
 * 登录逻辑一行没动:挂载即出码、并行取 login-options、轮询、过期换一张、
 * 演示登录由后端决定画不画。
 *
 * ── 三种打开方式,三条路(09-24 定)─────────────────────────────
 *
 * - 电脑:出二维码,手机微信扫。
 * - 微信内置浏览器:**自动**走网页授权(snsapi_base,不弹窗),打开即登录。
 *   手机上的工厂老板都是从服务号菜单或模板消息点进来的,本来就在微信里。
 * - 手机外部浏览器:**不出码** —— 码在自己屏幕上,扫不了。点「微信登录」弹一个说明:
 *   去微信里找服务号、点菜单进来。微信没有「从浏览器拉起授权再跳回浏览器」的口子,
 *   所以不在外部浏览器里登录,而是把人送回微信里那条路。
 */
export default function PortalLoginPage() {
  useTitle('登录 · 工厂端 · link4a');
  const { status } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const opts = useQuery({
    queryKey: ['portal-login-options'],
    queryFn: async (): Promise<LoginOptions> => {
      const res = await fetch('/api/tenant-auth/login-options', { credentials: 'include' });
      if (!res.ok) return { wechat_login: false };
      return (await res.json()) as LoginOptions;
    },
    // 读不到按「没有微信」画:少画一个入口只是不方便,画一个点了 404 的按钮是让人以为系统坏了
    retry: false,
  });

  useEffect(() => {
    if (status === 'authenticated') {
      navigate(params.get('next') ?? '/portal', { replace: true });
    }
  }, [status, params, navigate]);

  const wechat = opts.data?.wechat_login === true;
  // UA 在一次页面生命周期里不会变,算一次
  const [device] = useState(loginDevice);
  /**
   * 二维码跟 `login-options` 并行取:桌面上二维码就是首屏最大的元素(LCP),
   * 串着取白白多一次往返。没开通微信的租户会多打一次 404,可以接受。
   * 只有电脑出码:手机上出一张谁也扫不了的码,还白占一张票据。
   */
  const qr = useWechatQr(device === 'desktop');
  const next = params.get('next') ?? '/portal';
  const oauthHref = `/api/wechat/oauth/start?next=${encodeURIComponent(next)}`;
  const oauthFailed = params.has('wechat_error');

  /**
   * 微信里打开:不等人点,直接去授权。
   *
   * 两道闸防原地打转:后端授权失败会带 `wechat_error` 回来,见到就不自动;
   * 另外 30 秒内自动跳过一次就不再跳(失败方式不止后端那几种,
   * 比如服务号后台授权域名配错,微信直接在它自己的页面上报错,人按返回又回到这里)。
   */
  /** 自动授权没发出去(闸拦住了)才画按钮;否则微信里只看得到「正在登录」 */
  const [autoBlocked, setAutoBlocked] = useState(false);
  useEffect(() => {
    if (device !== 'wechat' || !wechat || status !== 'anonymous' || oauthFailed) return;
    if (!claimAutoOauth()) {
      setAutoBlocked(true);
      return;
    }
    window.location.replace(oauthHref);
  }, [device, wechat, status, oauthFailed, oauthHref]);
  /*
   * 从微信授权页按返回回来,页面可能是整个从缓存里恢复的(bfcache),
   * 上面那个 effect 不会再跑 —— 不处理的话就是一个永远转着的「正在登录」。
   */
  useEffect(() => {
    const onShow = (e: PageTransitionEvent): void => {
      if (e.persisted) setAutoBlocked(true);
    };
    window.addEventListener('pageshow', onShow);
    return () => window.removeEventListener('pageshow', onShow);
  }, []);
  /** 演示登录由后端决定画不画(`PORTAL_DEMO_LOGIN=1`),前端没有开关 */
  const demo = opts.data?.demo_login === true;

  return (
    <div
      className="grid min-h-[100dvh] bg-portal-ink font-portal lg:grid-cols-[minmax(0,1.35fr)_minmax(420px,0.85fr)]"
      data-testid="portal-login-page"
    >
      {/* 手机上登录卡在最上(order-first),桌面回到右栏 */}
      <div className="order-first flex min-h-[100dvh] items-center justify-center bg-portal-paper px-8 py-10 lg:order-last lg:min-h-0">
        <LoginPanel
          wechat={wechat}
          loading={opts.isLoading}
          demo={demo}
          qr={qr}
          device={device}
          oauthHref={oauthHref}
          oauthFailed={oauthFailed}
          showOauthButton={oauthFailed || autoBlocked}
          mpName={opts.data?.wechat_mp_name || '链上云帆'}
        />
      </div>

      <Showcase />
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   左栏:品牌 / 一句话 / 两张斜放的截图 / 点阵
   ══════════════════════════════════════════════════════════════ */

const SHOT_FRONT = '/portal-shots/value-conversation.png';
const SHOT_BACK = '/portal-shots/value-orders.png';

function Showcase(): JSX.Element {
  const shots = useRef<HTMLDivElement>(null);

  /**
   * 鼠标移动时两张图各转 ±3°。只在桌面、且用户没有要求减少动效时挂监听;
   * 直接改 style 而不走 state:mousemove 一秒几十次,走 React 重渲染是浪费。
   */
  useEffect(() => {
    const host = shots.current;
    if (!host || typeof window === 'undefined') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!window.matchMedia('(min-width: 1024px)').matches) return;
    const front = host.querySelector<HTMLElement>('[data-shot="front"]');
    const back = host.querySelector<HTMLElement>('[data-shot="back"]');
    if (!front || !back) return;
    const onMove = (e: MouseEvent): void => {
      const x = e.clientX / window.innerWidth - 0.5;
      const y = e.clientY / window.innerHeight - 0.5;
      front.style.transform = `rotateY(${14 + x * 6}deg) rotateX(${6 - y * 5}deg) rotateZ(-1.5deg)`;
      back.style.transform = `rotateY(${-10 + x * 6}deg) rotateX(${5 - y * 5}deg) translateZ(-120px)`;
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  return (
    <section className="relative flex min-w-0 flex-col justify-between overflow-hidden px-7 py-8 lg:min-h-[100dvh] lg:px-20 lg:py-14">
      <WaveField className="absolute inset-0 z-0 hidden h-full w-full lg:block" />

      <a
        href="https://link4a.com"
        className="relative z-10 flex items-center gap-3 text-portal-paper"
      >
        <span
          className="relative h-[30px] w-[30px] shrink-0 rounded-portal-md bg-gradient-to-br from-portal-accent to-portal-ink shadow-[inset_0_0_0_1px_rgb(255_255_255/0.16)]"
          aria-hidden
        >
          <span className="absolute left-2 top-2 h-2 w-2 rounded-full bg-portal-paper" />
          <span className="absolute bottom-[7px] right-[7px] h-1.5 w-1.5 rounded-full bg-portal-glow" />
        </span>
        <span className="text-portal-body font-bold tracking-wide">链上云帆</span>
        <span className="text-portal-meta font-semibold uppercase tracking-[0.22em] text-portal-mute">
          link4a
        </span>
      </a>

      <div className="relative z-10 my-auto pt-[6vh]">
        <h1 className="text-portal-display font-bold tracking-tight text-portal-paper [text-wrap:balance]">
          工厂自己的外贸客户端
        </h1>
        <p className="mt-4 text-portal-lg text-portal-mist">询盘、客户、订单进度，一个地方。</p>

        {/*
          透视舞台。桌面上高度按视口给、允许向右溢出 12vw(第二张被右栏切掉一截是刻意的出血);
          手机上只留第一张,不斜、100% 宽。
        */}
        <div
          ref={shots}
          className="relative mt-[3vh] lg:-mr-[12vw] lg:h-[min(56vh,560px)] lg:[perspective:1800px] lg:[perspective-origin:70%_40%]"
          data-testid="portal-login-shots"
        >
          <Shot
            src={SHOT_FRONT}
            alt="会话页"
            which="front"
            className="relative w-full lg:absolute lg:left-0 lg:top-0 lg:z-20 lg:w-[min(78%,900px)] lg:[transform:rotateY(14deg)_rotateX(6deg)_rotateZ(-1.5deg)]"
          />
          <Shot
            src={SHOT_BACK}
            alt="订单页"
            which="back"
            className="hidden lg:absolute lg:left-[60%] lg:top-[26%] lg:z-10 lg:block lg:w-[min(54%,640px)] lg:opacity-80 lg:[transform:rotateY(-10deg)_rotateX(5deg)_translateZ(-120px)]"
          />
        </div>
      </div>

      <p className="relative z-10 mt-10 flex items-center gap-4 text-portal-meta text-portal-mute lg:mt-0">
        <span>© 链上云帆</span>
        <a href="https://link4a.com" className="hover:text-portal-mist">
          link4a.com
        </a>
      </p>
    </section>
  );
}

/**
 * 一张截图:16:10 的框,图放到 150% 宽、对齐左上 —— 只露焦点区,字看得清。
 * 圆角 + 重投影 + 1px 白描边 + 蓝泛光在 `shadow-portal-shot` 里;表面一道斜向高光。
 */
function Shot({
  src,
  alt,
  which,
  className,
}: {
  src: string;
  alt: string;
  which: 'front' | 'back';
  className?: string;
}): JSX.Element {
  return (
    <figure
      data-shot={which}
      className={cn(
        'm-0 aspect-[16/10] overflow-hidden rounded-portal-lg bg-portal-paper shadow-portal-shot [transform-style:preserve-3d] [will-change:transform]',
        "after:pointer-events-none after:absolute after:inset-0 after:bg-[linear-gradient(110deg,transparent_40%,rgb(255_255_255/0.08)_60%,transparent_70%)] after:content-['']",
        className,
      )}
    >
      <img
        src={src}
        alt={alt}
        loading="eager"
        decoding="async"
        className="block h-auto w-[150%] max-w-none origin-top-left"
      />
    </figure>
  );
}

/* ══════════════════════════════════════════════════════════════
   右栏:登录。只有这几样,多一个字都不要
   ══════════════════════════════════════════════════════════════ */

function LoginPanel({
  wechat,
  loading,
  demo,
  qr,
  device,
  oauthHref,
  oauthFailed,
  showOauthButton,
  mpName,
}: {
  wechat: boolean;
  loading: boolean;
  demo: boolean;
  qr: WechatQr;
  device: LoginDevice;
  oauthHref: string;
  oauthFailed: boolean;
  showOauthButton: boolean;
  mpName: string;
}): JSX.Element {
  const [guideOpen, setGuideOpen] = useState(false);
  return (
    <section
      className="w-full max-w-[380px] text-center"
      aria-label="登录"
      data-testid="portal-login-panel"
    >
      <h2 className="text-portal-h1 font-bold tracking-tight text-portal-ink">登录</h2>
      <p className={cn(type.caption, 'mt-1.5')}>
        {device === 'desktop' ? '微信扫一扫' : '用微信登录'}
      </p>

      {/*
        三种状态(还在问后端 / 有微信 / 没微信)共用一个固定高的槽,
        骨架和真码同尺寸,出码前后不跳版(CLS)。
      */}
      <div className="mx-auto mt-7 min-h-[248px]">
        {loading ? (
          <div
            className="mx-auto h-[232px] w-[232px] animate-pulse rounded-portal-xl bg-portal-tint"
            data-testid="portal-login-loading"
          />
        ) : wechat ? (
          device === 'wechat' ? (
            /*
              微信内置浏览器里才走网页授权:`oauth2/authorize` 在别的浏览器里点它必挂。
              正常情况页面一打开就自动跳了,只画「正在登录」;
              按钮只在自动那次没发出去 / 失败回来时出现。
            */
            !showOauthButton ? (
              <div
                className="flex flex-col items-center pt-16"
                data-testid="portal-wechat-oauth-loading"
              >
                <span
                  className="h-8 w-8 animate-spin rounded-full border-2 border-portal-line border-t-portal-wechat"
                  aria-hidden
                />
                <p className={cn(type.caption, 'mt-3')}>正在登录…</p>
              </div>
            ) : (
              <div className="pt-16">
                {oauthFailed && (
                  <p className={cn(type.caption, 'mb-3')} data-testid="portal-wechat-oauth-failed">
                    没登上，再试一次
                  </p>
                )}
                <a
                  href={oauthHref}
                  data-testid="portal-wechat-login-btn"
                  className={cn(button.primary, button.block, wechatBtn)}
                >
                  用微信登录
                </a>
              </div>
            )
          ) : device === 'mobile' ? (
            <div className="pt-16">
              <button
                type="button"
                onClick={() => setGuideOpen(true)}
                data-testid="portal-wechat-mobile-btn"
                className={cn(button.primary, button.block, wechatBtn)}
              >
                微信登录
              </button>
            </div>
          ) : (
            <WechatQrLogin qr={qr} />
          )
        ) : (
          <p className={cn(banner.neutral, 'text-left')} data-testid="portal-wechat-unavailable">
            微信登录开通中，请用业务员发给你的登录链接进入。
          </p>
        )}
      </div>

      {wechat && device !== 'wechat' && (
        <p className={cn(type.caption, 'mt-8')}>
          没有微信？
          {/*
            落点是官网留资那一段里的那句「已经在合作？登录工厂端。没有登录方式，
            找对接你的业务员要一条链接」。以前指的是 link4a.com 首页 —— 而首页当时
            没有任何登录入口，工厂点过去转一圈又回到原地。#login-help 这个锚点
            由 apps/landing 那边的守卫钉着，别改成裸域名。
          */}
          <a href="https://link4a.com/#login-help" className="font-medium text-portal-accent">
            向对接你的业务员要登录链接
          </a>
        </p>
      )}

      <p className={cn(type.meta, 'mt-14')}>登录即表示你同意服务条款</p>

      {demo && <DemoLogin />}

      {guideOpen && <OpenInWechatGuide mpName={mpName} onClose={() => setGuideOpen(false)} />}
    </section>
  );
}

const wechatBtn = 'bg-portal-wechat hover:bg-portal-wechat-hover';

/**
 * 手机外部浏览器的「微信登录」:告诉人去微信里哪儿点,再帮他把微信打开。
 *
 * `weixin://` 只能把微信拉到前台,带不了任何参数 —— 进哪个服务号只能靠这句说明。
 * 拉不起来(没装微信、浏览器拦了)页面上什么都不会发生,说明还在,人照着做就行。
 */
function OpenInWechatGuide({
  mpName,
  onClose,
}: {
  mpName: string;
  onClose: () => void;
}): JSX.Element {
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-portal-ink/50 sm:items-center"
      onClick={onClose}
      data-testid="portal-wechat-guide"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="portal-wechat-guide-title"
        className="w-full max-w-[420px] rounded-t-portal-xl bg-portal-paper px-6 pb-8 pt-6 text-left sm:rounded-portal-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="portal-wechat-guide-title" className={type.h2}>
          在微信里打开
        </h3>
        <ol className={cn(type.body, 'mt-4 list-decimal space-y-2 pl-5')}>
          <li>
            打开微信，找到服务号「<b>{mpName}</b>」
          </li>
          <li>
            点底部菜单「<b>工厂端</b>」，自动登录
          </li>
        </ol>
        <a
          href="weixin://"
          className={cn(button.primary, button.block, wechatBtn, 'mt-6')}
          data-testid="portal-wechat-guide-open"
        >
          打开微信
        </a>
        <button type="button" onClick={onClose} className={cn(button.ghost, button.block, 'mt-2')}>
          取消
        </button>
      </div>
    </div>
  );
}

interface LoginOptions {
  wechat_login: boolean;
  demo_login?: boolean;
  /** 服务号名字,手机外部浏览器的说明里要让人去搜它 */
  wechat_mp_name?: string;
}

type LoginDevice = 'wechat' | 'mobile' | 'desktop';

/**
 * 在哪打开的。放在函数里是为了用例能连 UA 一起换掉。
 *
 * iPad 的 Safari 默认报桌面 UA(`Macintosh`),靠触点数认出来 ——
 * 它跟手机一样,扫不了自己屏幕上的码。
 */
function loginDevice(): LoginDevice {
  const ua = navigator.userAgent ?? '';
  if (/MicroMessenger/i.test(ua)) return 'wechat';
  if (/Android|iPhone|iPad|iPod|HarmonyOS|Mobile/i.test(ua)) return 'mobile';
  if (/Macintosh/i.test(ua) && (navigator.maxTouchPoints ?? 0) > 1) return 'mobile';
  return 'desktop';
}

const AUTO_OAUTH_KEY = 'portal-wechat-auto-oauth';

/** 30 秒内没自动跳过才放行,并记下这一次。存储读写失败时放行(最坏多跳一次) */
function claimAutoOauth(): boolean {
  try {
    const last = Number(sessionStorage.getItem(AUTO_OAUTH_KEY) ?? 0);
    if (Date.now() - last < 30_000) return false;
    sessionStorage.setItem(AUTO_OAUTH_KEY, String(Date.now()));
  } catch {
    // 隐私模式等拿不到 sessionStorage:照跳,wechat_error 那道闸还在
  }
  return true;
}

type QrState =
  | { phase: 'loading' }
  | { phase: 'ready'; ticket: string; qrUrl: string; pollMs: number }
  | { phase: 'expired' }
  | { phase: 'error'; message: string };

/**
 * PC 扫码登录(W1)。
 *
 * 页面出一张带参二维码 → 工厂用微信扫(顺带关注服务号)→ 微信把事件推给我们 →
 * 后端认出这个 openid 是谁 → 这边轮询到 `authorized` 就已经有 session 了。
 *
 * 轮询不用长连接:一次登录最多转几十次。停止条件写死三个(成功 / 待认领 / 过期),
 * 没有「一直转下去」的分支。挂载就出码;过期之后要人点「换一张」——
 * 那时候人多半已经走开了,自动续码等于让一台没人看的电脑一直要凭据。
 */
export interface WechatQr {
  state: QrState;
  restart: () => void;
}

function useWechatQr(enabled: boolean): WechatQr {
  const [state, setState] = useState<QrState>({ phase: 'loading' });
  // StrictMode 下 effect 跑两次,不挡的话首屏会连出两张码(后一张把前一张作废)
  const started = useRef(false);

  const start = async (): Promise<void> => {
    setState({ phase: 'loading' });
    try {
      const res = await fetch('/api/wechat/qrcode', { method: 'POST', credentials: 'include' });
      if (!res.ok) {
        setState({ phase: 'error', message: '微信登录暂时不可用，请用业务员发的登录链接' });
        return;
      }
      const body = (await res.json()) as {
        ticket?: string;
        qr_url?: string;
        poll_interval_ms?: number;
      };
      // 没有 ticket 就不进 ready:微信没开通时某些反代会回一个 200 的 JSON,进了 ready 会拿着 undefined 一直轮询
      if (!body.ticket || !body.qr_url) {
        setState({ phase: 'error', message: '微信登录暂时不可用，请用业务员发的登录链接' });
        return;
      }
      setState({
        phase: 'ready',
        ticket: body.ticket,
        qrUrl: body.qr_url,
        pollMs: body.poll_interval_ms || 2000,
      });
    } catch {
      setState({ phase: 'error', message: '网络不通，检查一下网络再试' });
    }
  };

  useEffect(() => {
    if (!enabled || started.current) return;
    started.current = true;
    void start();
  }, [enabled]);

  useEffect(() => {
    if (state.phase !== 'ready') return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const tick = async (): Promise<void> => {
      try {
        const res = await fetch(`/api/wechat/qrcode/${encodeURIComponent(state.ticket)}`, {
          credentials: 'include',
        });
        const body = (await res.json().catch(() => null)) as {
          status?: string;
          redirect?: string;
        } | null;
        if (stopped) return;
        // 401 = 过期 / 已用过:两种都只能重新出码
        if (!res.ok || body?.status === 'expired' || body?.status === 'consumed') {
          setState({ phase: 'expired' });
          return;
        }
        if (body?.status === 'authorized' || body?.status === 'unbound') {
          // 整页跳转不是 SPA 路由:AuthContext 在启动时读一次 /api/me,前端跳的话拿的还是「未登录」
          window.location.href = body.redirect ?? '/portal';
          return;
        }
      } catch {
        // 单次轮询失败不算失败(切了下网、页面在后台):下一拍继续
      }
      if (!stopped) timer = setTimeout(() => void tick(), state.pollMs);
    };

    timer = setTimeout(() => void tick(), state.pollMs);
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    };
  }, [state]);

  return { state, restart: () => void start() };
}

/**
 * 二维码框 232 见方,浅灰底 + 发丝线,下沿一枚「● 微信」小胶囊。
 * 三种状态共用同一个框:出码前后不跳版。加载态不画转圈(转圈让人以为要等很久)。
 */
function WechatQrLogin({ qr }: { qr: WechatQr }): JSX.Element {
  const { state, restart } = qr;
  return (
    <div className="relative mx-auto w-[232px]" data-testid="portal-wechat-qr">
      <div className="flex h-[232px] w-[232px] items-center justify-center rounded-portal-xl border border-portal-line bg-portal-tint">
        {state.phase === 'ready' ? (
          <img
            src={state.qrUrl}
            alt="微信登录二维码"
            width={196}
            height={196}
            className="h-[196px] w-[196px] rounded-portal-sm"
            data-testid="portal-wechat-qr-img"
          />
        ) : state.phase === 'expired' ? (
          <div className="text-center" data-testid="portal-wechat-qr-expired">
            <p className={type.caption}>二维码过期了</p>
            <button
              onClick={restart}
              className={cn(button.secondary, button.compact, 'mt-2')}
              data-testid="portal-wechat-qr-refresh"
            >
              换一张
            </button>
          </div>
        ) : state.phase === 'error' ? (
          <p className={cn(type.caption, 'px-4 text-center')} data-testid="portal-wechat-error">
            {state.message}
          </p>
        ) : (
          <div className="h-[196px] w-[196px] animate-pulse rounded-portal-sm bg-portal-tint2" />
        )}
      </div>
      <span className="absolute -bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1.5 whitespace-nowrap rounded-full border border-portal-line bg-portal-paper px-2.5 py-0.5 text-portal-meta text-portal-char">
        <i className="inline-block h-2 w-2 rounded-full bg-portal-wechat" aria-hidden />
        微信
      </span>
    </div>
  );
}

/**
 * 演示登录:输一个档位,直接以演示租户的身份进来(`PORTAL_DEMO_LOGIN=1` 才画)。
 * 进的是 `demo-free` / `demo-pro` 两个专门建的演示租户,跟真客户的数据没有关系。
 */
function DemoLogin(): JSX.Element {
  const [tier, setTier] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const go = async (): Promise<void> => {
    setBusy(true);
    setErr(null);
    try {
      const res = await fetch('/api/tenant-auth/demo-login', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ tier: tier.trim() }),
      });
      const body = (await res.json().catch(() => null)) as { message?: string } | null;
      if (!res.ok) {
        setErr(body?.message ?? '进不去，看一眼服务端日志');
        return;
      }
      // 整页刷新而不是 navigate:AuthContext 是启动时读一次 /api/me
      window.location.href = '/portal';
    } catch (e) {
      setErr(e instanceof Error ? e.message : '网络错误');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={cn(banner.warn, 'mt-8 flex-col items-stretch text-left')}
      data-testid="portal-demo-login"
    >
      <div className="font-medium">演示登录（临时）</div>
      <p className="mt-0.5">
        输 <b>0</b> 看免费档，<b>1</b> 看 Pro 档。用的是演示租户，不是真客户数据。
      </p>
      <div className="mt-2 flex gap-2">
        <input
          value={tier}
          onChange={(e) => setTier(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void go();
          }}
          placeholder="0 或 1"
          inputMode="numeric"
          aria-label="演示档位"
          className={cn(field.input, 'w-24 py-1.5')}
          data-testid="portal-demo-tier"
        />
        <button
          onClick={() => void go()}
          disabled={busy || !tier.trim()}
          className={cn(button.secondary, button.compact)}
          data-testid="portal-demo-go"
        >
          {busy ? '进入中…' : '进入'}
        </button>
      </div>
      {err && (
        <p className="mt-2" data-testid="portal-demo-error">
          {err}
        </p>
      )}
    </div>
  );
}
